import type { HexColor } from "./index.ts";
import type { LangCode } from "./meeting.ts";

export const SUPER_ADMIN_EMAILS: readonly string[] = [
  "moshe@echotora.com",
  "moshe157@gmail.com",
  "mmoshe157@gmail.com",
  "moshem@google.com",
  "avishitrit@google.com",
  "avi.shitri@gmail.com",
  "avi.shitrit@gmail.com",
] as const;

export function isSuperAdminEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const lower = email.trim().toLowerCase();
  return (SUPER_ADMIN_EMAILS as readonly string[]).includes(lower);
}

export type UserRole =
  | "Sales"
  | "Sales / AE"
  | "Sales / Account Executive (AE)"
  | "Sales Rep"
  | "Account Executive"
  | "Account Manager"
  | "BDR"
  | "SDR / BDR"
  | "Marketing"
  | "Customer Support & Success"
  | "Pre-Sale / Solutions Engineer"
  | "Pre-Sales / Solutions Engineer"
  | "Retention & Account Management"
  | "Executive / Management"
  | "Sr. Cloud SE"
  | "Cloud SE"
  | "Sales Manager"
  | "Customer Engineer"
  | "CE Security"
  | "CE Infra"
  | "CE AI"
  | "CE AppDev"
  | "CE Data"
  | "Solutions Architect"
  | "SE Manager"
  | "Outcome CE"
  | "AI FDE"
  | "AI OCE"
  | "Project Manager"
  | "Technical Executive"
  | "Business Executive"
  | (string & {});

export const DEFAULT_COMPANY_ROLES: readonly string[] = [
  "Sales / Account Executive (AE)",
  "Sales Rep",
  "BDR (Business Development)",
  "Account Manager",
  "Marketing",
  "Customer Support & Success",
  "Pre-Sales / Solutions Engineer",
  "Retention & Account Management",
  "Executive / Management",
] as const;

/**
 * The Customer Engineer roles — Google Cloud solution architects. "Customer Engineer"
 * is the generic CE; the "CE <specialty>" variants drive specialty-biased coaching
 * (hints, follow-ups, summary) and the live architecture-diagram tab. Adding a new CE
 * role here is all that's needed for it to pick up the coaching + diagram behavior.
 */
export const CE_ROLES: readonly UserRole[] = [
  "Customer Engineer",
  // Sr. Cloud SE is a technical engineer role — treat it like a CE (technical hints,
  // architecture diagrams, technical infographic).
  "Sr. Cloud SE",
  "CE Security",
  "CE Infra",
  "CE AI",
  "CE AppDev",
  "CE Data",
  // Outcome CE is a hands-on, technical post-sale role — gets the CE treatment
  // (technical hints, architecture diagrams), with an adoption/consumption bias.
  "Outcome CE",
  // AI delivery/outcome specialists — hands-on technical AI roles, treated as CEs.
  "AI FDE",
  "AI OCE",
] as const;

/** True when the role is any Customer Engineer variant. Shared by api + web so the
 * coaching directives and the Diagram-tab gating use one definition. */
export function isCERole(role: UserRole | string | undefined | null): boolean {
  return !!role && (CE_ROLES as readonly string[]).includes(role);
}

/** All selectable roles, in display order. Shared by Settings + meeting-setup selectors. */
export const ALL_ROLES: readonly UserRole[] = [
  "Account Executive",
  "Sales Rep",
  "BDR",
  "Account Manager",
  "Marketing",
  "Sales Manager",
  "Customer Support & Success",
  "Pre-Sales / Solutions Engineer",
  "Sr. Cloud SE",
  "Cloud SE",
  "Customer Engineer",
  "CE Security",
  "CE Infra",
  "CE AI",
  "CE AppDev",
  "CE Data",
  "Solutions Architect",
  "SE Manager",
  "Outcome CE",
  "AI FDE",
  "AI OCE",
  "Project Manager",
  "Technical Executive",
  "Business Executive",
] as const;

export type UserTeam =
  | "EMEA Cloud Sales"
  | "NAMER Cloud Sales"
  | "APAC Cloud Sales"
  | "LATAM Cloud Sales"
  | "Strategic Accounts";

export type UserLanguage =
  | "en"
  | "he"
  | "es"
  | "fr"
  | "de"
  | "pt-BR"
  | "ja"
  | "ko"
  | "zh-CN"
  | "ar"
  | "it"
  | "nl"
  | "ru"
  | "tr"
  | "hi";

export type Timezone =
  | "Europe/London"
  | "Asia/Jerusalem"
  | "America/New_York"
  | "America/Los_Angeles"
  | "Asia/Tokyo";

export type HintPace = "sparse" | "balanced" | "chatty";

/**
 * How the live coach decides when to surface hints. Controls both cadence and
 * the model's relevance bar (and how noisy screen-share insights are):
 * - `live`    — frequent hints, low bar (the original firehose behavior).
 * - `balanced`— slower, only when it clearly adds value.
 * - `focused` — rare, only genuinely critical moments.
 * Absent = `live`, so existing users see no change until they opt in.
 */
export type HintStrategy = "live" | "balanced" | "focused";

export interface UserProfile {
  name: string;
  role: UserRole;
  team: UserTeam;
  timezone: Timezone;
  email: string;
  initials: string;
  color: HexColor;
}

export interface UserSettings {
  language: UserLanguage;
  hintPace: HintPace;
  /** Live-coach hint strategy. Undefined = "live" (original behavior). The user
   * can change it in Settings or from the live meeting header; the last choice
   * sticks as their default. See {@link HintStrategy}. */
  hintStrategy?: HintStrategy;
  /** How eagerly live hints attach a Google-vs-alternative comparison table.
   * "aggressive" (default) = current behavior; "conservative" = only on an explicit
   * competitor mention / direct comparison. Undefined = "aggressive". */
  comparisonMode?: "aggressive" | "conservative";
  autoSummary: boolean;
  quietByDefault: boolean;
  keepTranscript?: boolean;
  layoutWidths?: { rail: number; coach: number; transcript: number };
  /** Per-user questions answered in the meeting summary's "Custom summary" block.
   * Undefined = fall back to DEFAULT_CUSTOM_QUESTIONS. Empty array = no custom block.
   * This remains the global default; per-role/per-type sets live in customQuestionSets. */
  customQuestions?: string[];
  /** Per-user custom-question sets keyed by CE role or meeting type (see
   * CUSTOM_QUESTION_PRESETS). Seeded from the preset on first edit; the user's edits are
   * saved here without touching the global {@link customQuestions}. */
  customQuestionSets?: Record<string, string[]>;
  /** Whether the custom-summary block is enabled. Undefined = on (default). */
  customSummaryEnabled?: boolean;
  /** The role the user last chose at meeting setup — the setup role selector defaults
   * to this (falls back to the profile role). Sticks until changed. */
  lastChosenRole?: UserRole;
  /** Default language for Sally's ON-SCREEN assistance (hints/answers), independent
   * of the spoken meeting language. "auto"/undefined = follow the spoken language.
   * New meetings default their assistanceLanguage to this; the live switcher and
   * setup selector update it so the last choice sticks. */
  assistanceLanguage?: "auto" | LangCode;
  /** Default SPOKEN/meeting language (what people talk in), used to bias STT and pin
   * the meeting language. "auto"/undefined = auto-detect. New meetings default their
   * language to this; the setup selector and the live meeting-language switcher update
   * it so the user's last choice sticks across meetings. Distinct from
   * {@link assistanceLanguage}. */
  spokenLanguage?: "auto" | LangCode;
  /** Set once the user has completed the first-login role + hints-language prompt,
   * so it isn't shown again. */
  onboardedRoleLang?: boolean;
  /** Preferred language for study materials generated in "My ongoing training"
   * (podcast script + TTS voice, slides, quiz, infographic text). "auto"/undefined =
   * follow {@link assistanceLanguage}, else the UI {@link language}, else English. */
  trainingLanguage?: "auto" | LangCode;
  /** Repeated-question suggestions the user has dismissed (canonical text, lower-cased
   * on compare) — never suggested again. See {@link SuggestedQuestion}. */
  dismissedQuestionSuggestions?: string[];
  /** Client (account) names the user has "deleted" from the Clients page. Their
   * meetings are kept, but the client is hidden from the Clients grid. */
  hiddenClients?: string[];
  /** IDs of approved Marketplace plugins the user has enabled (opt-in). Enabled
   * plugins render in the Plugins tab on the live/summary screens. */
  enabledPluginIds?: string[];
  /** Name of the Google Drive folder that "Export to Drive" writes meeting Markdown
   * into. Undefined = "Sally Meeting Exports". The folder is app-created in the user's
   * own Drive (drive.file scope); renaming here targets a different folder on next export. */
  driveFolderName?: string;
  /** Whether the floating voice onboarding agent bubble is shown across the app.
   * Undefined = on (default). It runs on the user's own Gemini key (Gemini Live). */
  onboardingAgentEnabled?: boolean;
}

/** A question the rep has asked repeatedly across recent meetings, surfaced as a
 * suggestion to add to their saved custom questions. */
export interface SuggestedQuestion {
  /** Canonical phrasing of the recurring question. */
  question: string;
  /** How many of the recent meetings it appeared in. */
  count: number;
}

/**
 * Default questions for the per-user custom summary block. Used when a user has
 * not configured their own list. Focused on the kind of company/founder facts a
 * rep wants going into (or recapping) a meeting.
 */
export const DEFAULT_CUSTOM_QUESTIONS: string[] = [
  "Who are the founders?",
  "How much money have they raised?",
  "Where are they located?",
  "What does the startup do?",
  "What is the background of the founders?",
  "What is their website?",
  "What are the founders' LinkedIn profiles?",
];

/**
 * Suggested custom-question sets a user can pick per meeting, keyed by CE role or by
 * meeting type. These are the seeds — a user's edits are saved under
 * {@link UserSettings.customQuestionSets} without changing these defaults.
 */
export const CUSTOM_QUESTION_PRESETS: Record<string, string[]> = {
  // --- CE role presets ---
  "CE Security": [
    "What compliance frameworks must they meet (FedRAMP, ISO, SOC2, PCI)?",
    "How do they manage identity and access today (IdP, SSO, MFA)?",
    "What are their top security/threat concerns right now?",
    "Do they have a SIEM/SOC, and which tooling (Splunk, Chronicle, Sentinel)?",
    "Any data residency / sovereignty requirements?",
  ],
  "CE Infra": [
    "What's their current compute footprint (VMs, Kubernetes, on-prem)?",
    "What's driving a migration or modernization now?",
    "What are their networking/connectivity requirements (hybrid, interconnect)?",
    "What are their scale, latency, and availability targets?",
    "Any VMware or legacy estate to migrate?",
  ],
  "CE AI": [
    "What AI/ML use cases are they pursuing?",
    "What models/frameworks do they use today (in-house, OpenAI, etc.)?",
    "Do they have an MLOps / data pipeline in place?",
    "What are their data volumes and where does the data live?",
    "What's blocking their GenAI adoption (cost, skills, governance)?",
  ],
  "CE AppDev": [
    "What's their app stack and deployment model today?",
    "How do they do CI/CD and release management?",
    "What databases/back-ends power their apps?",
    "Are they containerized/serverless, or looking to move there?",
    "What developer-productivity pain points do they have?",
  ],
  "CE Data": [
    "What's their current data warehouse / lake (Snowflake, Redshift, on-prem)?",
    "What are their main analytics and reporting workloads?",
    "How do they do data ingestion and ETL/ELT today?",
    "What are their data volumes and freshness requirements?",
    "Any governance / lineage / cost concerns with their current platform?",
  ],
  "Customer Engineer": [
    "What are their top technical priorities this year?",
    "What's their current cloud footprint (GCP / AWS / Azure / on-prem)?",
    "What's the main initiative driving this engagement?",
    "Who are the technical decision-makers?",
    "What would success look like for them?",
  ],
  "Outcome CE": [
    "Which committed workloads are we ramping, and what's the consumption target/timeline?",
    "What's blocking production adoption right now (technical, org, or skills)?",
    "Who owns delivery — customer, partner, or Google Cloud Consulting — and where are the handoff gaps?",
    "What does 'in production and consuming' look like for this workload (the success metric)?",
    "Which adoption milestones are at risk, and what unblocks them fastest?",
  ],
  "Project Manager": [
    "What are the key milestones and the target timeline?",
    "What's the scope, and what's explicitly out of scope?",
    "What are the top risks, blockers, and dependencies right now?",
    "Who owns each workstream, and where are the resourcing gaps?",
    "What are the acceptance criteria / definition of done for this phase?",
  ],
  "Technical Executive": [
    "What is the target architecture and the key platform decisions?",
    "What are the main technical risks, constraints, and scalability limits?",
    "How does this align with their technical strategy and existing stack?",
    "What are the build-vs-buy and integration trade-offs?",
    "What are the security, compliance, and operational (SLO/observability) requirements?",
  ],
  "Business Executive": [
    "What business outcome or KPI are they trying to move?",
    "What is the expected ROI, cost impact, and total cost of ownership?",
    "What is the decision timeline and the budget/approval process?",
    "Who are the executive sponsors and decision-makers?",
    "What are the top business risks if they do — or don't — act?",
  ],
  // --- Meeting-type presets ---
  technical: [
    "What architecture was proposed or discussed?",
    "What are the latency / scale / availability requirements?",
    "What integrations and data flows are involved?",
    "What technical risks or constraints were raised?",
    "What are the next technical steps / POC criteria?",
  ],
  sales: DEFAULT_CUSTOM_QUESTIONS,
  onboarding: [
    "What are their success criteria for onboarding?",
    "Who are the key stakeholders and their roles?",
    "What's the target go-live timeline?",
    "What existing systems must integrate?",
    "What training or enablement do they need?",
  ],
};

/**
 * Resolve which custom-question set to use for a meeting: the user's edited set for the
 * key, else the preset for the key, else the user's global list, else the defaults.
 */
export function resolveCustomQuestions(
  key: string | undefined,
  settings: Pick<UserSettings, "customQuestionSets" | "customQuestions"> | undefined,
): string[] {
  if (key && settings?.customQuestionSets?.[key]) return settings.customQuestionSets[key];
  if (key && CUSTOM_QUESTION_PRESETS[key]) return CUSTOM_QUESTION_PRESETS[key];
  if (settings?.customQuestions && settings.customQuestions.length > 0)
    return settings.customQuestions;
  return DEFAULT_CUSTOM_QUESTIONS;
}

export type UserStatus = "pending" | "approved" | "blocked";

/**
 * Gamification membership tier:
 * - "gold" — awarded manually by an admin (top contributors / VIPs).
 * - "silver" — earned automatically the first time a user submits feedback.
 * - "regular" — the default for everyone else (undefined tier === "regular").
 * Only an admin can promote a user to (or between) tiers; the app never downgrades
 * Gold. Gold/Silver see a member badge on their dashboard; Regular sees nothing. */
export type UserTier = "gold" | "silver" | "regular";

export interface User extends UserProfile {
  uid: string;
  settings: UserSettings;
  /** The user's saved Gemini API key (bring-your-own), remembered server-side so
   * it persists across sessions and devices. Only ever returned to the owner via
   * /users/me. Undefined = no key saved. */
  geminiKey?: string;
  geminiKeyVerified: boolean;
  /** How the user obtained their API key, recorded during onboarding:
   * - "personal" — saved their own key.
   * - "none" — clicked "I don't have a key yet" (surfaced to admins so they can
   *   grant a shared key).
   * - "shared"/"managed-project" — provisioned via the shared/BYO-project flows.
   * Undefined = legacy user (never went through the onboarding gate). */
  apiKeySource?: "personal" | "none" | "shared" | "managed-project";
  createdAt: string;
  updatedAt: string;
  /** Access-control status. New signups are "pending" until an admin approves;
   * admins can "blocked" a user. Undefined = legacy/grandfathered (approved). */
  status?: UserStatus;
  /** ISO timestamp of when a new user first requested access. */
  requestedAt?: string;
  /** VIP: when true, the user may use the app's shared Gemini key (auto-provisioned)
   * instead of creating their own. Set by an admin (at invite or on the users table). */
  useSharedKey?: boolean;
  /** AIMS: when true, the user may access the Interview (AIMS) section. Defaults to
   * false for every user until an admin turns it on. Independent of `status` so a
   * user can hold sales + interview access concurrently (dual-role). */
  interviewAccess?: boolean;
  /** AIMS: admin convenience flag granting sales + interview access at once
   * (implies status="approved" and interviewAccess=true). */
  fullAccess?: boolean;
  /** Gamification membership tier. Undefined = "regular". See {@link UserTier}. */
  tier?: UserTier;
  /** Organization / Company ID the user belongs to. */
  companyId?: string;
  /** Organization / Company display name. */
  companyName?: string;
  /** Role within the organization ("admin" | "member"). The first user in a company is automatically admin. */
  companyRole?: CompanyRole;
  /** Functional job role in the company (e.g. "Sales", "Customer Support", "Pre-Sale", etc.) */
  functionalRole?: string;
  /** Roles configured for the user's company/organization */
  companyRoles?: string[];
  /** Allowed data connectors the user can see/access. If undefined, all connected sources are available. */
  allowedConnectors?: ConnectorType[];
  /** UID of the user's manager / supervisor in the company hierarchy. */
  managerUid?: string;
  /** Name of the manager (cached for display). */
  managerName?: string;
  /** Email of the manager. */
  managerEmail?: string;
  /** True if the user is a platform-wide super admin. */
  isSuperAdmin?: boolean;
}

export type CompanyRole = "admin" | "member";

export type ConnectorType = "googleDrive" | "monday" | "sharepoint" | "uploads";

export interface CompanyInvite {
  email: string;
  name?: string;
  companyRole: CompanyRole;
  functionalRole?: string;
  managerUid?: string;
  managerName?: string;
  allowedConnectors?: ConnectorType[];
  invitedByUid: string;
  invitedAt: string;
}

export interface OrganizationIntegrations {
  monday?: {
    connected: boolean;
    accountName?: string;
    userEmail?: string;
    apiKey?: string;
    connectedAt?: string;
  };
  sharepoint?: {
    connected: boolean;
    siteUrl?: string;
    userEmail?: string;
    connectedAt?: string;
  };
  googleDrive?: {
    connected: boolean;
    connectedAt?: string;
  };
}

export interface Organization {
  id: string;
  name: string;
  domain?: string;
  subdomain?: string;
  allowedDomains?: string[];
  description?: string;
  productsServices?: string;
  valueProposition?: string;
  targetAudience?: string;
  competitors?: string;
  pitchKeywords?: string;
  roles?: string[];
  proceduresText?: string;
  storeName?: string;
  integrations?: OrganizationIntegrations;
  /** Explicitly invited members exempt from domain restrictions */
  invitedMembers?: CompanyInvite[];
  createdAt: string;
  createdByUid: string;
}

export interface TenantInfo {
  id: string;
  name: string;
  subdomain: string;
  domain?: string;
  allowedDomains?: string[];
  invitedMembers?: string[];
  description?: string;
  productsServices?: string;
  roles?: string[];
  logoUrl?: string;
}

export interface CompanyDocument {
  id: string;
  organizationId: string;
  name: string;
  contentType: string;
  category?: "procedure" | "knowledge" | "general";
  source?: "upload" | "gdrive" | "monday" | "sharepoint" | "web";
  sizeBytes?: number;
  gcsPath?: string;
  sourceUrl?: string;
  fileSearchDoc?: string;
  status: "ready" | "processing" | "failed";
  createdAt: string;
  uploadedByEmail: string;
}

export interface TeamMember {
  uid: string;
  name: string;
  role: string;
  initials: string;
  color: HexColor;
  email: string;
  companyRole?: CompanyRole;
  functionalRole?: string;
  managerUid?: string;
  managerName?: string;
  managerEmail?: string;
  allowedConnectors?: ConnectorType[];
  external?: boolean;
  online?: boolean;
  status?: "active" | "invited";
  tasksCount?: number;
  meetingsCount?: number;
}
